import type { FC } from 'react';
import { Alert, Empty, Flex, Spin, Typography } from 'antd';
import { EvimoPagination } from '~/app/components/common/EvimoTable/EvimoPagination';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WORK_ORDER_UI } from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import { ApplyMergeDialog } from '../ApplyMergeDialog/ApplyMergeDialog';
import { OptimizerClusterCard } from '../OptimizerClusterCard/OptimizerClusterCard';
import type { PendingApplyMerge } from '../../types';
import type { OptimizerClusterView } from '../../types';
import { clusterKey } from '../../utils';
import { WORK_ORDER_OPTIMIZER_PATH } from '../../constants';

export interface OptimizerSuggestionsBodyProps {
  clusters: OptimizerClusterView[];
  canManage: boolean;
  isLoading: boolean;
  isError: boolean;
  total: number;
  page: number;
  pageSize: number;
  showPagination?: boolean;
  showPreviewFooter?: boolean;
  previewLimit?: number;
  pendingApply: PendingApplyMerge | null;
  isApplying: boolean;
  onPageChange: (page: number) => void;
  onSelectWorkOrder: (workOrderId: string) => void;
  onOpenApply: (cluster: OptimizerClusterView) => void;
  onPendingTargetChange: (id: string) => void;
  onCancelApply: () => void;
  onConfirmApply: () => void;
  /** Preserve site filter when linking to the full optimizer page. */
  siteFilter?: string;
}

export const OptimizerSuggestionsBody: FC<OptimizerSuggestionsBodyProps> = ({
  clusters,
  canManage,
  isLoading,
  isError,
  total,
  page,
  pageSize,
  showPagination = false,
  showPreviewFooter = false,
  previewLimit,
  pendingApply,
  isApplying,
  onPageChange,
  onSelectWorkOrder,
  onOpenApply,
  onPendingTargetChange,
  onCancelApply,
  onConfirmApply,
  siteFilter,
}) => {
  const { t } = useTranslation('maintenance');
  const shown = clusters.length;
  const hasMoreThanPreview =
    showPreviewFooter && total > (previewLimit ?? shown);

  const viewAllTo = siteFilter
    ? `${WORK_ORDER_OPTIMIZER_PATH}?siteId=${encodeURIComponent(siteFilter)}`
    : WORK_ORDER_OPTIMIZER_PATH;

  return (
    <>
      {isError ? (
        <Alert
          type="error"
          title={t('workOrders.kanban.optimizer.loadFailed')}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <Spin spinning={isLoading}>
        {clusters.length === 0 && !isLoading ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('workOrders.kanban.optimizer.empty')}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              alignItems: 'stretch',
              gap: 12,
              overflowX: 'auto',
              paddingBottom: 4,
            }}
          >
            {clusters.map((cluster) => (
              <OptimizerClusterCard
                key={clusterKey(cluster)}
                cluster={cluster}
                canManage={canManage}
                onApply={() => onOpenApply(cluster)}
                onSelectWorkOrder={onSelectWorkOrder}
              />
            ))}
          </div>
        )}
      </Spin>

      {showPreviewFooter && total > 0 ? (
        <Flex
          align="center"
          justify="space-between"
          gap={12}
          wrap="wrap"
          style={{ marginTop: 12 }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {t('workOrders.kanban.optimizer.previewShowing', {
              shown,
              total,
            })}
          </Typography.Text>
          {hasMoreThanPreview ? (
            <Link
              to={viewAllTo}
              style={{
                fontWeight: 600,
                color: WORK_ORDER_UI.primary,
                fontSize: 13,
              }}
            >
              {t('workOrders.kanban.optimizer.viewAll', { total })}
            </Link>
          ) : null}
        </Flex>
      ) : null}

      {showPagination && total > pageSize ? (
        <EvimoPagination
          style={{ marginTop: 16, width: '100%' }}
          current={page}
          pageSize={pageSize}
          total={total}
          onChange={onPageChange}
          showSizeChanger={false}
        />
      ) : null}

      <ApplyMergeDialog
        open={pendingApply != null}
        cluster={pendingApply?.cluster ?? null}
        targetWorkOrderId={pendingApply?.targetWorkOrderId ?? null}
        loading={isApplying}
        onTargetChange={onPendingTargetChange}
        onCancel={onCancelApply}
        onConfirm={onConfirmApply}
      />
    </>
  );
};
